import type { MediaKind, UploadMediaResultDto } from "@mandoob/shared";

import { appConfig, getAccessToken } from "@/config";
import { api } from "@/lib/api-client";

// Media helpers shared by the composer and the message bubbles. Ported from
// the reference project's chat-composer.tsx (image re-encode) and trimmed to
// what Mandoob's upload contract needs.

/** Hard cap enforced by POST /api/inbox/upload-media. */
export const MEDIA_MAX_BYTES = 25 * 1024 * 1024;

// Images above this size get re-encoded down to (at or below) it before
// uploading; smaller ones only get re-encoded when oversized or in a format
// WhatsApp rejects.
const IMAGE_COMPRESS_THRESHOLD_BYTES = 5 * 1024 * 1024;
// Longest edge after re-encoding — WhatsApp thumbnails don't need more.
const IMAGE_MAX_EDGE = 1600;

// WhatsApp only accepts PNG and JPEG for image messages; anything else (WebP,
// GIF, BMP, HEIC…) is re-encoded to JPEG before uploading.
const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Turns the DTO's proxy path (`/api/inbox/media/{messageId}`) into a URL an
 * <img>/<video>/<audio> src or download link can load: prefixes the API base
 * (for the native shell) and appends the JWT as `?token=` since media elements
 * can't send an Authorization header.
 */
export function mediaSrc(mediaUrl: string): string {
  const absolute = /^https?:\/\//i.test(mediaUrl)
    ? mediaUrl
    : `${appConfig.apiBaseUrl}${mediaUrl}`;
  const token = getAccessToken();
  if (!token) return absolute;
  const separator = absolute.includes("?") ? "&" : "?";
  return `${absolute}${separator}token=${encodeURIComponent(token)}`;
}

/**
 * Multipart upload to POST /api/inbox/upload-media (field name "file"). The
 * api client drops its default JSON content type for FormData bodies so the
 * browser sets the multipart boundary itself.
 */
export function uploadMedia(
  file: File | Blob,
  fileName: string,
): Promise<UploadMediaResultDto> {
  const form = new FormData();
  form.append("file", file, fileName);
  return api<UploadMediaResultDto>({
    url: "/api/inbox/upload-media",
    method: "POST",
    data: form,
  });
}

/** SendMessageInput.mediaKind for a prepared file's MIME type. */
export function mediaKindForMime(mime: string): MediaKind {
  const type = mime.toLowerCase();
  if (type.startsWith("image/")) return "Image";
  if (type.startsWith("video/")) return "Video";
  if (type.startsWith("audio/")) return "Audio";
  return "Document";
}

const stripExtension = (name: string) => name.replace(/\.[^./\\]+$/, "");

// Loads a File into a decoded HTMLImageElement, cleaning up the object URL.
const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image could not be loaded"));
    };
    image.src = url;
  });

// Encodes an image onto a canvas at the given scale and returns the blob.
const encodeImage = (
  image: HTMLImageElement,
  type: string,
  scale: number,
  quality?: number,
): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve(null);
      return;
    }
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

/**
 * Re-encodes an image as JPEG capped at ~1600px on the longest edge, stepping
 * quality (then dimensions) down until the result fits the compress threshold.
 * Images that are already small, correctly sized and in a supported format are
 * returned untouched; any failure falls back to the original file so a broken
 * canvas never blocks the upload.
 */
/** Longest edge for label-scan photos — plenty for OCR, small Gemini payload. */
const LABEL_MAX_EDGE = 1500;

/**
 * Shipping-label photos: always re-encoded to JPEG capped at 1500×1500 before
 * going to the extraction endpoint. Same canvas pipeline as the chat input;
 * any failure falls back to the original file.
 */
export async function prepareLabelImage(file: File): Promise<File> {
  try {
    const image = await loadImage(file);
    const longEdge = Math.max(image.naturalWidth, image.naturalHeight);
    const scale = longEdge > LABEL_MAX_EDGE ? LABEL_MAX_EDGE / longEdge : 1;
    const blob = await encodeImage(image, "image/jpeg", scale, 0.85);
    if (!blob) return file;
    const baseName = stripExtension(file.name) || "label";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export async function prepareImageForUpload(file: File): Promise<File> {
  const type = file.type.toLowerCase();
  if (!type.startsWith("image/")) return file;
  try {
    const image = await loadImage(file);
    const longEdge = Math.max(image.naturalWidth, image.naturalHeight);
    const supported = SUPPORTED_IMAGE_TYPES.includes(type);
    const needsWork =
      !supported ||
      longEdge > IMAGE_MAX_EDGE ||
      file.size > IMAGE_COMPRESS_THRESHOLD_BYTES;
    if (!needsWork) return file;

    const baseScale = longEdge > IMAGE_MAX_EDGE ? IMAGE_MAX_EDGE / longEdge : 1;
    const baseName = stripExtension(file.name) || "image";
    let smallest: Blob | null = null;
    for (const scale of [1, 0.85, 0.7, 0.55, 0.4]) {
      for (const quality of [0.82, 0.65, 0.5]) {
        const blob = await encodeImage(
          image,
          "image/jpeg",
          baseScale * scale,
          quality,
        );
        if (!blob) continue;
        if (!smallest || blob.size < smallest.size) smallest = blob;
        if (blob.size <= IMAGE_COMPRESS_THRESHOLD_BYTES) {
          return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
        }
      }
    }
    if (!smallest) return file;
    return new File([smallest], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}
